/**
 * @file Input component for color input
 * @author Heino Lenting, Maarten Lensink
 */
import * as tslib_1 from "tslib";
import { DropdownInput } from '../../base/inputs/input-dropdown/input-dropdown.component';
import { ColorTools } from '../../base/helpers/color-tools';
import { ColorValidator, NAME_COLOR_VALIDATOR } from '../../core/validators/color.validator';
import { Validator } from 'breeze-client';
/**
 * Class for input component for color input
 */
var InputColorComponent = /** @class */ (function (_super) {
    tslib_1.__extends(InputColorComponent, _super);
    function InputColorComponent() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this._entity = undefined;
        _this.colorPattern = 'assignment';
        _this.parentColor = '#C0C0C0';
        _this.hasParent = false;
        /** Set of color tools */
        _this.colorTools = new ColorTools();
        /**
         * Pre-set color set
         * From: http://godsnotwheregodsnot.blogspot.nl/2012/09/color-distribution-methodology.html
         * Orderning via: http://jsfiddle.net/shanfan/ojgp5718/
         */
        _this.colors = [
            '#C8A1A1', '#FF4A46', '#BA0900', '#7B4F4B', '#FFAA92', '#B77B68', '#A05837', '#772600',
            '#FDE8DC', '#FF913F', '#D16100', '#C0B9B2', '#938A81', '#FAD09F', '#886F4C', '#7A4900',
            '#B79762', '#A77500', '#FFB500', '#FFF69F', '#575329', '#9B9700', '#FFFF00', '#61615A',
            '#4FC601', '#3B9700', '#1E6E00', '#A1C299', '#456648', '#04F757', '#63FFAC', '#008941',
            '#FEFFE6', '#BEC459', '#6B7900', '#A4E804', '#788D66', '#C2FF99', '#1B4400', '#549E79',
            '#0CBD66', '#C2FFED', '#00FECF', '#00C2A0', '#00846F', '#809693', '#004D43', '#00A6AA',
            '#A3C8C9', '#1CE6FF', '#0089A3', '#456D75', '#0AA6D8', '#006FA6', '#404E55', '#8CD0FF',
            '#0086ED', '#DDEFFF', '#99ADC0', '#00489C', '#324E72', '#7A87A1', '#8FB0FF', '#3B5DFF',
            '#6367A9', '#A079BF', '#7900D7', '#B4A8BD', '#4A3B53', '#72418F', '#D790FF', '#BC23FF',
            '#BC23FF', '#EEC3FF', '#FF34FF', '#B903AA', '#6F0062', '#885578', '#D157A0', '#A30059',
            '#FF90C9', '#6B002C', '#FF2F80', '#6A3A4C', '#997D87', '#CB7E98', '#CC0744', '#FFDBE5',
            '#B05B6F', '#FF8A9A'
        ];
        return _this;
    }
    Object.defineProperty(InputColorComponent.prototype, "entity", {
        /**
         * Gets the entity with a color property
         */
        get: function () {
            return this._entity;
        },
        /**
         * Sets the entity with a color property
         */
        set: function (value) {
            this._entity = value;
            if (!value || !value.entityType) {
                return;
            }
            var parent = null;
            InputColorComponent.possibleParentTypes.forEach(function (possibility) {
                if (value[possibility] != null) {
                    parent = value[possibility];
                }
            });
            if (parent !== null && parent['Color'] !== '#FFFFFF') {
                this.parentColor = parent['Color'];
            }
            switch (value.entityType.shortName) {
                case 'TaskTemplate':
                case 'Task':
                case 'Assignment':
                    // Assignments met waarde true voor de property Appointment worden binnen HTML5 als Appointments gezien.
                    this.colorPattern = this.entity['Appointment'] ? 'appointment' : 'assignment';
                    break;
                case 'Appointment':
                    // Eerder hielde we rekening met Appointments die geen Absence als reden hadden.
                    // Van deze Symagic Server mogelijkheid wordt geen gebruik gemaakt.
                    // Appointments behoren nu functioneel altijd een Absence als reden te hebben.
                    this.colorPattern = 'absence';
                    break;
                default:
                    this.colorPattern = 'assignment';
                    break;
            }
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Validates a value
     * @param newValue The value to be validated
     * @returns A value indicating whether the value is valid
     * @returns {ValidationErrors} has errors if value is false
     * @memberOf InputColorComponent
     */
    InputColorComponent.prototype.isValidValue = function (newValue) {
        var _a;
        if (ColorValidator().validate(newValue)) {
            var errorKey = NAME_COLOR_VALIDATOR;
            return _a = {},
                _a[errorKey] = Validator.messageTemplates[errorKey],
                _a;
        }
        return null;
    };
    /**
     * Returns the default control validator for color
     * @protected
     * @returns {(ValidatorFn | ValidatorFn[])}
     */
    InputColorComponent.prototype.defaultControlValidator = function () {
        var self = this;
        return function (c) {
            return self.isValidValue(c.value);
        };
    };
    /**
     * Parses a new value into its appropriate type
     * @param newValue The value to be parsed
     * @returns The parsed value
     */
    InputColorComponent.prototype.parseValue = function (newValue) {
        if (typeof newValue === 'string' && newValue.charAt(0) !== '#') {
            newValue = '#' + newValue;
        }
        return newValue.toUpperCase();
    };
    /**
     * Returns the color to be displayed
     * @returns The parent color if the value is white, otherwise the value itself
     */
    InputColorComponent.prototype.displayColor = function () {
        return this.value === '#FFFFFF' ? this.parentColor : this.value;
    };
    /**
     * Sets the value to the selected color and closes the dropdown
     * @param color The string representation of the color to be set
     */
    InputColorComponent.prototype.setColor = function (color) {
        this.value = color;
        this.setClose();
    };
    Object.defineProperty(InputColorComponent.prototype, "isDraft", {
        /**
         * Gets a value indicating whether the entity is in draft or final
         */
        get: function () {
            return this.entity && this.entity.Draft;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns the font color for the text input
     * @returns {string} The font color for the text input
     */
    InputColorComponent.prototype.getFontColor = function () {
        if (this._validationErrors && this._validationErrors.length) {
            return null;
        }
        return this.colorPattern === 'absence' ? this.colorTools.darken(this.displayColor(), 20) : this.colorTools.contrast(this.displayColor());
    };
    /**
     * Returns the input border color
     * @returns {string} The input border color
     */
    InputColorComponent.prototype.getBorderColor = function () {
        if (this._validationErrors && this._validationErrors.length) {
            return null;
        }
        return this.colorPattern === 'absence' ? this.displayColor() : this.colorTools.darken(this.displayColor(), 20);
    };
    /**
     * Returns the highest contrasting color (black or white) of the parent color if the value is white
     */
    InputColorComponent.prototype.getDropdownBorderParentColor = function () {
        if (this.value !== null && this.value.toUpperCase() !== '#FFFFFF') {
            return this.parentColor;
        }
        return this.colorTools.contrast(this.parentColor);
    };
    /**
     * Returns the highest contrasting color (black or white) if the color is equal to the value
     * @param color The selectable color in the dropdown
     * @returns The highest contrasting color if the parameter is equal to the value, otherwise the parameter is returned
     */
    InputColorComponent.prototype.getDropdownBorderColor = function (color) {
        // If given color is unequal to color, return color (no border is visible)
        if (this.value !== null && this.value.toUpperCase() !== color.toUpperCase()) {
            return color;
        }
        else {
            return this.colorTools.contrast(color);
        }
    };
    InputColorComponent.possibleParentTypes = ['Deployment', 'Demand', 'Task', 'Project', 'Service', 'Shift'];
    return InputColorComponent;
}(DropdownInput));
export { InputColorComponent };
